import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useDashboardTheme } from "@/pages/MeetingDashboard/themeHelper";

interface DataItem {
  name?: string;
  email?: string;
  isVerified?: boolean;
  status?: "active" | "inactive";
  role?: string;
  userImg?: string;
  month?: string;
  date?: string;
  used?: number;
  tokenused?: number;
}

interface Props {
  data: DataItem[];
  customFrom: string;
  customTo: string;
  filter: string;
  setCustomTo: (val: string) => void;
  setCustomFrom: (val: string) => void;
  setFilter: (val: "yearly" | "monthly" | "custom") => void;
  selectedYear: number;
  setSelectedYear: (val: number) => void;
  selectedMonth: number;
  setSelectedMonth: (val: number) => void;
  isTenant?: boolean;
}

const MAX_CUSTOM_RANGE_DAYS = 60;

const UserCandleGraph: React.FC<Props> = ({
  data,
  customFrom,
  customTo,
  filter,
  setCustomTo,
  setCustomFrom,
  setFilter,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  setSelectedMonth,
  isTenant = false,
}) => {
  const { isDark, colors } = useDashboardTheme();
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (
      filter === "custom" &&
      customFrom &&
      customTo &&
      dayjs.isDayjs(customFrom) &&
      dayjs.isDayjs(customTo)
    ) {
      const from = moment(customFrom);
      const to = moment(customTo);
      const diff = to.diff(from, "days");
      if (diff > MAX_CUSTOM_RANGE_DAYS) {
        setErrorMessage(
          `Date range too long. Please select up to ${MAX_CUSTOM_RANGE_DAYS} days.`,
        );
      } else if (diff < 0) {
        setErrorMessage("Invalid date range. 'From' must be before 'To'.");
      } else {
        setErrorMessage("");
      }
    } else {
      setErrorMessage("");
    }
  }, [customFrom, customTo, filter]);

  let categories: string[] = [];
  let usedValues: number[] = [];
  const MAX_BARS = 100;
  const tooManyBars = data.length > MAX_BARS;
  if (filter === "yearly") {
    categories = data.map((item) => item.month || "");
  } else if (filter === "monthly" || filter === "custom") {
    categories = data.map((item) => moment(item.date).format("DD"));
  }

  usedValues = data.map((item) =>
    isTenant ? (item.tokenused ?? 0) : (item.used ?? 0),
  );

  const chartData = {
    options: {
      chart: { id: "bar-chart", toolbar: { show: false } },
      xaxis: {
        categories,
        labels: {
          style: { colors: colors.text, fontSize: "11px" },
        },
      },
      yaxis: {
        labels: {
          style: { colors: colors.text, fontSize: "11px" },
        },
      },
      tooltip: { theme: isDark ? ("dark" as const) : ("light" as const) },
      colors: [isDark ? "#818CF8" : "#4F46E5"],
      grid: { borderColor: colors.border },
    },
    series: [
      { name: isTenant ? "Tokens Used" : "Licence Usage", data: usedValues },
    ],
  };

  return (
    <div
      style={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        borderRadius: "16px",
        boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 15px rgba(0,0,0,0.05)",
        color: colors.text,
        padding: "20px",
        margin: "0 auto",
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        mb={2}
        gap={2}
      >
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel sx={{ color: colors.textMuted }}>Filter</InputLabel>
          <Select
            value={filter}
            onChange={(e) =>
              setFilter(e.target.value as "yearly" | "monthly" | "custom")
            }
            label="Filter"
            sx={{
              color: colors.text,
              ".MuiOutlinedInput-notchedOutline": { borderColor: colors.border },
              "& .MuiSvgIcon-root": { color: colors.text },
            }}
          >
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        {filter === "yearly" && (
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel sx={{ color: colors.textMuted }}>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              label="Year"
              sx={{
                color: colors.text,
                ".MuiOutlinedInput-notchedOutline": { borderColor: colors.border },
                "& .MuiSvgIcon-root": { color: colors.text },
              }}
            >
              {Array.from({ length: 5 }, (_, i) => moment().year() - i).map(
                (year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        )}

        {filter === "monthly" && (
          <>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel sx={{ color: colors.textMuted }}>Month</InputLabel>
              <Select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                label="Month"
                sx={{
                  color: colors.text,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: colors.border },
                  "& .MuiSvgIcon-root": { color: colors.text },
                }}
              >
                {moment.months().map((monthName, index) => (
                  <MenuItem key={monthName} value={index + 1}>
                    {monthName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }} size="small">
              <InputLabel sx={{ color: colors.textMuted }}>Year</InputLabel>
              <Select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                label="Year"
                sx={{
                  color: colors.text,
                  ".MuiOutlinedInput-notchedOutline": { borderColor: colors.border },
                  "& .MuiSvgIcon-root": { color: colors.text },
                }}
              >
                {Array.from({ length: 5 }, (_, i) => moment().year() - i).map(
                  (year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          </>
        )}

        {filter === "custom" && (
          <>
            <DatePicker
              label="From"
              value={customFrom ? dayjs(customFrom) : null}
              onChange={(newValue) =>
                setCustomFrom(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              format="MM/DD/YY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  placeholder: "MM/DD/YY",
                  size: "small",
                  inputProps: {
                    placeholder: "MM/DD/YY",
                  },
                  InputProps: {
                    sx: {
                      color: colors.text,
                      "& input": {
                        color: `${colors.text} !important`,
                        "&::placeholder": {
                          color: `${colors.textMuted} !important`,
                          opacity: 1,
                        },
                      },
                      "& fieldset": {
                        borderColor: `${colors.border} !important`,
                      },
                      "& .MuiSvgIcon-root": {
                        color: `${colors.text} !important`,
                      },
                    },
                  },
                  InputLabelProps: {
                    sx: {
                      color: `${colors.textMuted} !important`,
                    },
                  },
                },
              }}
            />

            <DatePicker
              label="To"
              value={customTo ? dayjs(customTo) : null}
              onChange={(newValue) =>
                setCustomTo(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              format="MM/DD/YY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  placeholder: "MM/DD/YY",
                  size: "small",
                  inputProps: {
                    placeholder: "MM/DD/YY",
                  },
                  InputProps: {
                    sx: {
                      color: colors.text,
                      "& input": {
                        color: `${colors.text} !important`,
                        "&::placeholder": {
                          color: `${colors.textMuted} !important`,
                          opacity: 1,
                        },
                      },
                      "& fieldset": {
                        borderColor: `${colors.border} !important`,
                      },
                      "& .MuiSvgIcon-root": {
                        color: `${colors.text} !important`,
                      },
                    },
                  },
                  InputLabelProps: {
                    sx: {
                      color: `${colors.textMuted} !important`,
                    },
                  },
                },
              }}
            />
          </>
        )}
      </Box>

      {errorMessage && (
        <Typography variant="body2" color="error" mb={2}>
          {errorMessage}
        </Typography>
      )}

      {!errorMessage && tooManyBars && (
        <Typography variant="body2" color="warning.main" mb={2}>
          Too much data to display. Please narrow your date range.
        </Typography>
      )}

      {!errorMessage && !tooManyBars && (
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height="350"
        />
      )}
    </div>
  );
};

export default UserCandleGraph;
